// SSE client for the Borg conversation stream endpoint.
// Uses fetch() with ReadableStream instead of EventSource to support
// custom headers (CSRF token, cookies) through the same proxy path.
// useNamingConvention is disabled for this file via biome.mjs override.

import type { BorgStreamEvent, BorgStreamEventMetadata } from "../models/borg-stream-types";

import { parseBorgStreamEvent } from "./_borg-stream-parser";
import { borgApiPath } from "./borg-api";

const EVENT_PREFIX = "event: ";
const DATA_PREFIX = "data: ";

interface StreamCallbacks {
	onEvent: (event: BorgStreamEvent, metadata?: BorgStreamEventMetadata) => void;
	onError: (error: Error) => void;
	onDone: () => void;
}

/**
 * Connects to the Borg SSE stream for a conversation.
 * Returns an AbortController to disconnect.
 */
export function connectBorgStream(
	conversationId: string,
	onEvent: (event: BorgStreamEvent, metadata?: BorgStreamEventMetadata) => void,
	onError: (error: Error) => void,
	onDone: () => void,
): AbortController {
	const callbacks: StreamCallbacks = { onEvent, onError, onDone };
	const controller = new AbortController();

	const url = `${borgApiPath(`/conversations/${encodeURIComponent(conversationId)}/stream`)}?stream_version=2`;

	// Read CSRF token from cookie (same logic as request.ts)
	const csrfToken = document.cookie
		.split(";")
		.find((c) => c.trim().startsWith("_sts_csrf="))
		?.split("=")[1];

	const headers: HeadersInit = {
		Accept: "text/event-stream",
	};
	if (csrfToken) {
		(headers as globalThis.Record<string, string>)["X-Sts-Token"] = csrfToken;
	}

	startStream({
		url,
		headers,
		controller,
		callbacks,
	});

	return controller;
}

interface StartStreamOptions {
	url: string;
	headers: HeadersInit;
	controller: AbortController;
	callbacks: StreamCallbacks;
}

function startStream({ url, headers, controller, callbacks }: StartStreamOptions): void {
	globalThis
		.fetch(url, {
			headers,
			signal: controller.signal,
			credentials: "same-origin",
		})
		.then((response) => {
			if (!response.ok) {
				throw new Error(`SSE stream error: ${response.status}`);
			}
			if (!response.body) {
				throw new Error("SSE stream: no response body");
			}
			return readSSEStream({
				body: response.body,
				signal: controller.signal,
				callbacks,
			});
		})
		.catch((err) => {
			if (err instanceof DOMException && err.name === "AbortError") {
				return; // intentional disconnect
			}
			callbacks.onError(err instanceof Error ? err : new Error(String(err)));
		});
}

interface SSEParseState {
	event: string;
	data: string;
}

interface ReadSSEStreamOptions {
	body: ReadableStream<Uint8Array>;
	signal: AbortSignal;
	callbacks: StreamCallbacks;
}

/**
 * Reads an SSE stream line by line and dispatches parsed events.
 */
async function readSSEStream({ body, signal, callbacks }: ReadSSEStreamOptions): Promise<void> {
	const reader = body.getReader();
	const decoder = new TextDecoder();
	let buffer = "";
	const state: SSEParseState = { event: "", data: "" };

	let isDone = false;
	const guardedOnDone = () => {
		if (!isDone) {
			isDone = true;
			callbacks.onDone();
		}
	};

	const processChunk = (value: Uint8Array): void => {
		buffer += decoder.decode(value, { stream: true });
		const lines = buffer.split("\n");
		buffer = lines.pop() ?? "";

		for (const line of lines) {
			processSSELine(line, state, callbacks, guardedOnDone);
		}
	};

	const readNextChunk = async (): Promise<void> => {
		if (signal.aborted) {
			return;
		}
		const { done, value } = await reader.read();
		if (done) {
			return;
		}
		processChunk(value);
		await readNextChunk();
	};

	try {
		await readNextChunk();
	} finally {
		reader.releaseLock();
	}

	guardedOnDone();
}

function processSSELine(line: string, state: SSEParseState, callbacks: StreamCallbacks, onDone: () => void): void {
	if (line.startsWith(EVENT_PREFIX)) {
		state.event = line.slice(EVENT_PREFIX.length).trim();
	} else if (line.startsWith(DATA_PREFIX)) {
		state.data = line.slice(DATA_PREFIX.length);
	} else if (line === "" && state.event !== "") {
		dispatchSSEEvent(state.event, state.data, callbacks, onDone);
		state.event = "";
		state.data = "";
	}
	// Lines starting with ":" are SSE comments (keepalive) — ignore
}

function dispatchSSEEvent(eventType: string, data: string, callbacks: StreamCallbacks, onDone: () => void): void {
	const parsedEvent = parseBorgStreamEvent(eventType, data);
	if (parsedEvent == null) {
		return;
	}
	callbacks.onEvent(parsedEvent.event, parsedEvent.metadata);
	if (parsedEvent.event.type === "done") {
		onDone();
	}
}
